import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import DiagramRenderer from "@/components/csat/DiagramRenderer";
import {
  Clock, Timer, ArrowLeft, ArrowRight, CheckCircle, XCircle,
  Flag, Crown, ShieldCheck, AlertTriangle, TrendingUp, Target,
  Award, RotateCcw, ChevronRight, Zap, History, Eye, BarChart3,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const fmtClock = (secs) => {
  if (secs < 0) secs = 0;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const CSATTestSeries = ({ isPro, onBack }) => {
  const { toast } = useToast();
  const [phase, setPhase] = useState("intro"); // intro | test | submitting | results
  const [loading, setLoading] = useState(false);

  const [test, setTest] = useState(null); // {test_id, questions, ...}
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120 * 60);
  const [result, setResult] = useState(null); // {summary, analysis, review}
  const [reviewIdx, setReviewIdx] = useState(0);
  const [history, setHistory] = useState([]);
  const [loadingResult, setLoadingResult] = useState(false);

  const startedAtRef = useRef(null);
  const timerRef = useRef(null);
  const qStartRef = useRef(Date.now());
  const perQTimeRef = useRef({});
  const submittingRef = useRef(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/csat/test-series/history`, authHeader());
      setHistory(res.data.tests || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (isPro) loadHistory(); }, [isPro, loadHistory]);

  const viewPastResult = async (testId) => {
    setLoadingResult(true);
    try {
      const res = await axios.get(`${API_BASE}/csat/test-series/${testId}`, authHeader());
      setResult(res.data); setReviewIdx(0); setPhase("results");
    } catch (e) {
      toast({ title: "Could not open result", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setLoadingResult(false); }
  };

  const startTest = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/csat/test-series/start`, {}, authHeader());
      setTest(res.data);
      setAnswers({});
      setFlagged({});
      setCurrentQ(0);
      setTimeLeft((res.data.duration_minutes || 120) * 60);
      startedAtRef.current = Date.now();
      qStartRef.current = Date.now();
      perQTimeRef.current = {};
      setPhase("test");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start the test. Please try again.";
      toast({ title: "Unable to start test", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const recordQTime = useCallback((qid) => {
    if (!qid) return;
    const spent = Math.round((Date.now() - qStartRef.current) / 1000);
    perQTimeRef.current[qid] = (perQTimeRef.current[qid] || 0) + spent;
    qStartRef.current = Date.now();
  }, []);

  const goToQ = (idx) => {
    if (!test) return;
    recordQTime(test.questions[currentQ]?.id);
    setCurrentQ(idx);
  };

  const submitTest = useCallback(async (auto = false) => {
    if (submittingRef.current || !test) return;
    submittingRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    recordQTime(test.questions[currentQ]?.id);
    setPhase("submitting");
    const timeTaken = Math.round((Date.now() - startedAtRef.current) / 1000);
    try {
      const res = await axios.post(
        `${API_BASE}/csat/test-series/submit/${test.test_id}`,
        { answers, time_taken_seconds: timeTaken, per_question_seconds: perQTimeRef.current },
        authHeader()
      );
      setResult(res.data);
      setReviewIdx(0);
      setPhase("results");
      loadHistory();
      if (auto) toast({ title: "Time's up!", description: "Your test was submitted automatically." });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Submission failed. Please try again.";
      toast({ title: "Submission error", description: msg, variant: "destructive" });
      setPhase("test");
      submittingRef.current = false;
    }
  }, [test, answers, currentQ, recordQTime, toast]);

  // Countdown timer
  useEffect(() => {
    if (phase !== "test") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          submitTest(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [phase, submitTest]);

  // ─────────────────────────── INTRO / PAYWALL ───────────────────────────
  if (phase === "intro") {
    if (!isPro) {
      return (
        <div className="min-h-screen bg-slate-50/40 py-10 px-4">
          <div className="max-w-2xl mx-auto">
            <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="test-series-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to CSAT Suite
            </Button>
            <Card className="border-2 border-amber-200 shadow-xl" data-testid="test-series-paywall">
              <CardContent className="p-8 text-center space-y-4">
                <div className="mx-auto w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <Badge className="bg-amber-100 text-amber-800 border-0">CLARIAS PRO ONLY</Badge>
                <h2 className="text-2xl font-bold text-slate-900">CSAT Timed Test Series</h2>
                <p className="text-slate-600">
                  Simulate the full <b>80-question, 120-minute</b> CSAT paper with UPSC negative
                  marking and a CLARIAS behaviour-analytics report — clear-cutoff prediction,
                  time leaks, and the one behaviour to fix next.
                </p>
                <Button
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                  onClick={() => (window.location.href = "/upgrade")}
                  data-testid="test-series-upgrade-btn"
                >
                  Upgrade to PRO <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50/40 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="test-series-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to CSAT Suite
          </Button>
          <Card className="border-indigo-100 shadow-lg" data-testid="test-series-intro">
            <CardContent className="p-8 space-y-6">
              <div className="text-center space-y-2">
                <div className="mx-auto w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Timer className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">CSAT Full-Length Test</h1>
                <p className="text-slate-600">Exam-hall simulation • This is rehearsal, not practice.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Target, label: "80 Questions", sub: "Realistic CSAT mix" },
                  { icon: Clock, label: "120 Minutes", sub: "Strict countdown" },
                  { icon: AlertTriangle, label: "+2.5 / −0.83", sub: "UPSC marking" },
                  { icon: ShieldCheck, label: "66 / 200", sub: "33% qualifying" },
                ].map((it, i) => (
                  <div key={i} className="p-4 bg-white rounded-xl border border-slate-200 flex items-center gap-3">
                    <it.icon className="w-6 h-6 text-indigo-600" />
                    <div>
                      <p className="font-bold text-slate-900 text-sm">{it.label}</p>
                      <p className="text-xs text-slate-500">{it.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-900 space-y-1">
                <p className="font-semibold">Before you begin:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>The timer auto-submits when 120 minutes elapse.</li>
                  <li>Skipped questions carry no penalty — skip low-confidence ones.</li>
                  <li>You get a CLARIAS behaviour report + full solutions after submission.</li>
                </ul>
              </div>

              <Button
                className="w-full h-12 text-base bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
                onClick={startTest}
                disabled={loading}
                data-testid="test-series-start-btn"
              >
                {loading ? "Building your test…" : "Start Test"}
                {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
              </Button>
            </CardContent>
          </Card>

          {/* My CSAT Tests — history + score trend (CLARIAS intelligence) */}
          {history.length > 0 && (
            <div className="mt-6 space-y-4" data-testid="csat-test-history">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-slate-900">My CSAT Tests</h2>
                <Badge className="bg-slate-100 text-slate-600 border-0">{history.length}</Badge>
              </div>

              {history.length >= 2 && (
                <Card className="border-indigo-100">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 className="w-4 h-4 text-indigo-600" />
                      <p className="text-sm font-semibold text-slate-700">Score Trend (out of 200)</p>
                    </div>
                    <div className="h-40" data-testid="csat-test-trend-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={[...history].reverse().map((t, i) => ({
                          name: `T${i + 1}`,
                          score: t.summary?.score ?? 0,
                        }))} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                          <YAxis domain={[0, 200]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                          <Line type="monotone" dataKey="score" stroke="#4f46e5" strokeWidth={3} dot={{ r: 4, fill: "#4f46e5" }} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-2">
                {history.map((t) => {
                  const s = t.summary || {};
                  const dt = t.completed_at ? new Date(t.completed_at) : null;
                  return (
                    <Card
                      key={t.test_id}
                      className="border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => viewPastResult(t.test_id)}
                      data-testid={`csat-history-item-${t.test_id}`}
                    >
                      <CardContent className="p-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shrink-0">
                            <Award className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate">CSAT Full-Length Test</p>
                            <p className="text-xs text-slate-400">
                              {dt ? dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : ""}
                              {" · "}{s.correct ?? 0}/{s.total ?? 80} correct · {s.accuracy ?? 0}%
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {t.behaviour_label && (
                            <Badge className="bg-slate-100 text-slate-600 border-0 hidden sm:inline-flex">{t.behaviour_label}</Badge>
                          )}
                          <div className="text-right">
                            <p className="text-lg font-black text-indigo-700 leading-none">{s.score ?? 0}</p>
                            <p className="text-[10px] text-slate-400">/ {s.max_score ?? 200}</p>
                          </div>
                          <Eye className="w-4 h-4 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────── SUBMITTING ───────────────────────────
  if (phase === "submitting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4" />
          <p className="text-lg font-semibold text-slate-900">Scoring your paper…</p>
          <p className="text-sm text-slate-500">Generating your CLARIAS behaviour report.</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────── TEST (timed) ───────────────────────────
  if (phase === "test" && test) {
    const q = test.questions[currentQ];
    const answeredCount = Object.keys(answers).length;
    const lowTime = timeLeft <= 300;

    return (
      <div className="min-h-screen bg-slate-50/40">
        {/* Sticky timer bar */}
        <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className="bg-indigo-100 text-indigo-800 border-0">CSAT Full Test</Badge>
              <span className="text-sm text-slate-500 hidden sm:inline">
                {answeredCount}/{test.num_questions} answered
              </span>
            </div>
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono font-bold text-lg ${
                lowTime ? "bg-red-100 text-red-700 animate-pulse" : "bg-slate-100 text-slate-800"
              }`}
              data-testid="test-series-timer"
            >
              <Clock className="w-5 h-5" />
              {fmtClock(timeLeft)}
            </div>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => submitTest(false)}
              data-testid="test-series-submit-btn"
            >
              Submit Test
            </Button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_240px] gap-6">
          {/* Question column */}
          <div className="space-y-4">
            <Card key={q.id} className="border-indigo-100 shadow-sm" data-testid="test-series-question-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-indigo-100 text-indigo-800">Q {currentQ + 1} / {test.num_questions}</Badge>
                  <button
                    onClick={() => setFlagged({ ...flagged, [q.id]: !flagged[q.id] })}
                    className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border ${
                      flagged[q.id] ? "bg-amber-100 border-amber-300 text-amber-800" : "border-slate-200 text-slate-500"
                    }`}
                    data-testid="test-series-flag-btn"
                  >
                    <Flag className="w-4 h-4" /> {flagged[q.id] ? "Marked" : "Mark for review"}
                  </button>
                </div>

                <div className="text-xs text-slate-500 mb-3">{q.section_label}{q.chapter ? ` • ${q.chapter}` : ""}</div>

                {q.diagram && <DiagramRenderer diagram={q.diagram} reveal={false} />}

                <p className="text-lg text-slate-900 mb-6 leading-relaxed whitespace-pre-wrap">{q.question}</p>

                <div className="space-y-3">
                  {q.options?.map((opt) => {
                    const selected = answers[q.id] === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => setAnswers({ ...answers, [q.id]: opt.key })}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          selected ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-indigo-300 hover:bg-slate-50"
                        }`}
                        data-testid={`test-series-option-${opt.key}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            selected ? "bg-indigo-500 text-white" : "bg-slate-100 text-slate-600"
                          }`}>{opt.key.toUpperCase()}</span>
                          <span className="text-slate-700">{opt.text}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-6">
                  <Button variant="outline" disabled={currentQ === 0} onClick={() => goToQ(currentQ - 1)} data-testid="test-series-prev-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                  {answers[q.id] && (
                    <Button variant="ghost" className="text-slate-500" onClick={() => { const a = { ...answers }; delete a[q.id]; setAnswers(a); }}>
                      Clear
                    </Button>
                  )}
                  <Button
                    disabled={currentQ === test.num_questions - 1}
                    onClick={() => goToQ(currentQ + 1)}
                    className="bg-indigo-600 text-white"
                    data-testid="test-series-next-btn"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Palette */}
          <div className="lg:sticky lg:top-20 h-fit">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-slate-500 mb-3">QUESTION PALETTE</p>
                <div className="grid grid-cols-6 lg:grid-cols-5 gap-2" data-testid="test-series-palette">
                  {test.questions.map((qq, idx) => {
                    const isAns = !!answers[qq.id];
                    const isFlag = !!flagged[qq.id];
                    return (
                      <button
                        key={qq.id}
                        onClick={() => goToQ(idx)}
                        className={`relative w-9 h-9 rounded-lg text-xs font-bold ${
                          currentQ === idx
                            ? "bg-indigo-600 text-white ring-2 ring-indigo-300"
                            : isAns
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"
                        }`}
                        data-testid={`test-series-palette-${idx}`}
                      >
                        {idx + 1}
                        {isFlag && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full" />}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 space-y-1.5 text-xs text-slate-500">
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Answered</div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-white border border-slate-300" /> Not answered</div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-500" /> Marked</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────── RESULTS ───────────────────────────
  if (phase === "results" && result) {
    const { summary, analysis, review } = result;
    const rv = review[reviewIdx] || {};
    const steps = (rv.solution_steps && rv.solution_steps.length)
      ? rv.solution_steps
      : (rv.explanation ? rv.explanation.split("\n").filter(Boolean) : []);
    const qualified = summary.qualified;

    return (
      <div className="min-h-screen bg-slate-50/40 py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Button variant="ghost" onClick={onBack} data-testid="test-series-results-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to CSAT Suite
          </Button>

          {/* Score hero */}
          <Card className={`border-2 ${qualified ? "border-emerald-200" : "border-rose-200"} shadow-lg`} data-testid="test-series-result-hero">
            <CardContent className="p-8 text-center space-y-3">
              <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center ${qualified ? "bg-emerald-100" : "bg-rose-100"}`}>
                {qualified ? <Award className="w-8 h-8 text-emerald-600" /> : <AlertTriangle className="w-8 h-8 text-rose-600" />}
              </div>
              <p className="text-5xl font-black text-slate-900" data-testid="test-series-score">{summary.score}<span className="text-2xl text-slate-400"> / {summary.max_score}</span></p>
              <Badge className={qualified ? "bg-emerald-100 text-emerald-800 border-0" : "bg-rose-100 text-rose-800 border-0"}>
                {qualified ? "✓ Above qualifying cutoff (66)" : "✗ Below qualifying cutoff (66)"}
              </Badge>
              <p className="text-slate-600 max-w-lg mx-auto">{analysis?.verdict}</p>
              <div className="grid grid-cols-4 gap-2 pt-3 max-w-md mx-auto text-sm">
                <div><p className="text-xl font-bold text-emerald-600">{summary.correct}</p><p className="text-xs text-slate-500">Correct</p></div>
                <div><p className="text-xl font-bold text-rose-600">{summary.wrong}</p><p className="text-xs text-slate-500">Wrong</p></div>
                <div><p className="text-xl font-bold text-slate-500">{summary.skipped}</p><p className="text-xs text-slate-500">Skipped</p></div>
                <div><p className="text-xl font-bold text-indigo-600">{summary.accuracy}%</p><p className="text-xs text-slate-500">Accuracy</p></div>
              </div>
            </CardContent>
          </Card>

          {/* CLARIAS behaviour report */}
          {analysis && (
            <Card className="border-indigo-100" data-testid="test-series-analysis">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-slate-900">CLARIAS Behaviour Report</h3>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <div className="text-center px-5 py-3 bg-indigo-50 rounded-xl">
                    <p className="text-3xl font-black text-indigo-700">{analysis.behaviour_score}</p>
                    <p className="text-xs text-slate-500">Behaviour Score</p>
                  </div>
                  <div>
                    <Badge className="bg-slate-100 text-slate-700 border-0 mb-1">{analysis.behaviour_label}</Badge>
                    <p className="text-sm text-slate-600">Cutoff zone: <b>{analysis.cutoff_zone}</b></p>
                  </div>
                </div>

                {analysis.components && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(analysis.components).map(([k, v]) => (
                      <div key={k} className="p-3 bg-white border border-slate-200 rounded-xl">
                        <p className="text-lg font-bold text-slate-900">{v}</p>
                        <p className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</p>
                      </div>
                    ))}
                  </div>
                )}

                {analysis.primary_failure_reason && (
                  <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                    <p className="text-xs font-semibold text-rose-700 mb-1">WHERE YOU LOST MARKS</p>
                    <p className="text-sm text-slate-800">{analysis.primary_failure_reason}</p>
                  </div>
                )}
                {analysis.one_behaviour_to_fix && (
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700 mb-1">ONE BEHAVIOUR TO FIX</p>
                    <p className="text-sm text-slate-800">{analysis.one_behaviour_to_fix}</p>
                  </div>
                )}

                {/* Section-wise */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2">SECTION-WISE ACCURACY</p>
                  <div className="space-y-2">
                    {summary.section_wise?.map((s) => (
                      <div key={s.section} className="flex items-center gap-3">
                        <span className="text-sm text-slate-700 w-56 truncate">{s.label}</span>
                        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${s.accuracy >= 60 ? "bg-emerald-500" : s.accuracy >= 40 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${s.accuracy}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-24 text-right">{s.correct}/{s.total} • {s.accuracy}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                {analysis.confidence_message && (
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 flex items-start gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p className="text-sm text-emerald-900">{analysis.confidence_message}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Solution review */}
          <Card className="border-slate-200" data-testid="test-series-review">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900">Solution Review</h3>
                <span className="text-sm text-slate-500">Q {reviewIdx + 1} / {review.length}</span>
              </div>

              {/* review palette */}
              <div className="flex flex-wrap gap-1.5">
                {review.map((r, idx) => (
                  <button
                    key={idx}
                    onClick={() => setReviewIdx(idx)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold ${
                      reviewIdx === idx ? "ring-2 ring-indigo-400 " : ""
                    }${r.user_answer == null ? "bg-slate-100 text-slate-500" : r.is_correct ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}`}
                    data-testid={`test-series-review-nav-${idx}`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              <div key={rv.question_id} className="border-t pt-4">
                <div className="text-xs text-slate-500 mb-2">{rv.section_label}{rv.chapter ? ` • ${rv.chapter}` : ""}</div>
                {rv.diagram && <DiagramRenderer diagram={rv.diagram} reveal={true} />}
                <p className="text-base text-slate-900 mb-4 leading-relaxed whitespace-pre-wrap">{rv.question}</p>

                <div className="space-y-2">
                  {rv.options?.map((opt) => {
                    const isCorrect = opt.key === rv.correct_answer;
                    const isSel = opt.key === rv.user_answer;
                    return (
                      <div
                        key={opt.key}
                        className={`p-3 rounded-xl border-2 flex items-center justify-between ${
                          isCorrect ? "border-green-500 bg-green-50" : isSel ? "border-red-500 bg-red-50" : "border-slate-200"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-bold text-slate-500 uppercase">{opt.key}.</span>
                          <span className="text-slate-700 text-sm">{opt.text}</span>
                        </span>
                        {isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                        {isSel && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                      </div>
                    );
                  })}
                </div>

                {(steps.length > 0 || rv.formula_used || rv.shortcut_method || rv.common_mistake) && (
                  <div className="mt-4 space-y-3" data-testid="test-series-explanation">
                    {steps.length > 0 && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 mb-2">SOLUTION</p>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-slate-800">
                          {steps.map((s, i) => (<li key={i} className="whitespace-pre-wrap">{String(s).replace(/^\d+[.)]\s*/, "")}</li>))}
                        </ol>
                      </div>
                    )}
                    {rv.formula_used && (
                      <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-200">
                        <p className="text-xs font-semibold text-indigo-700 mb-1">FORMULA USED</p>
                        <p className="text-sm text-slate-800 font-mono whitespace-pre-wrap">{rv.formula_used}</p>
                      </div>
                    )}
                    {rv.shortcut_method && (
                      <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                        <p className="text-xs font-semibold text-emerald-700 mb-1">⚡ SMART APPROACH / SHORTCUT</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{rv.shortcut_method}</p>
                      </div>
                    )}
                    {rv.common_mistake && (
                      <div className="p-3 bg-rose-50 rounded-xl border border-rose-200">
                        <p className="text-xs font-semibold text-rose-700 mb-1">COMMON MISTAKE</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{rv.common_mistake}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between mt-5">
                  <Button variant="outline" disabled={reviewIdx === 0} onClick={() => setReviewIdx(reviewIdx - 1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Prev
                  </Button>
                  <Button disabled={reviewIdx === review.length - 1} onClick={() => setReviewIdx(reviewIdx + 1)} className="bg-indigo-600 text-white">
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center gap-3 pb-6">
            <Button
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
              onClick={() => { setResult(null); submittingRef.current = false; setPhase("intro"); }}
              data-testid="test-series-retake-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Take Another Test
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CSATTestSeries;
